import React, { useState, useEffect } from 'react';
import { sanitizeFormInput, sanitizeEmail, detectSuspiciousInput } from '../../utils/inputSanitization';
import './ValidatedInput.scss';

const ValidatedInput = ({
  type = 'text',
  name,
  value,
  onChange,
  placeholder,
  validation,
  className = '',
  style = {},
  disabled = false,
  showValidation = true,
  ...props
}) => {
  const [validationState, setValidationState] = useState({
    isValid: true,
    message: '',
    isDirty: false
  });
  const [isFocused, setIsFocused] = useState(false);

  // Real-time validation
  useEffect(() => {
    if (!validation) return;

    // Clear validation state when input is empty
    if (!value) {
      setValidationState({
        isValid: true,
        message: '',
        isDirty: false
      });
      return;
    }

    const validate = async () => {
      try {
        const result = await validation(value);
        setValidationState({
          isValid: result === null || result === true,
          message: result || '',
          isDirty: true
        });
      } catch (error) {
        setValidationState({
          isValid: false,
          message: error.message || 'Validation error',
          isDirty: true
        });
      }
    };

    validate();
  }, [value, validation]);

  const handleChange = (e) => {
    let sanitizedValue = e.target.value;
    
    // Apply appropriate sanitization based on input type
    if (type === 'email') {
      sanitizedValue = sanitizeEmail(e.target.value);
    } else {
      sanitizedValue = sanitizeFormInput(e.target.value);
    }

    // Check for suspicious patterns
    const suspicious = detectSuspiciousInput(sanitizedValue);
    if (suspicious.isSuspicious) {
      console.warn(`Suspicious input detected: ${suspicious.reason}`);
    }

    // Call the parent onChange with the original event structure
    onChange({
      target: {
        name: e.target.name,
        value: sanitizedValue
      }
    });
  };

  const handleFocus = (e) => {
    setIsFocused(true);
    if (props.onFocus) props.onFocus(e);
  };

  const handleBlur = (e) => {
    setIsFocused(false);
    if (props.onBlur) props.onBlur(e);
  };

  const getInputClasses = () => {
    const classes = ['validated-input'];
    
    if (validationState.isDirty) {
      classes.push(validationState.isValid ? 'valid' : 'invalid');
    }
    
    if (isFocused) {
      classes.push('focused');
    }
    
    if (disabled) {
      classes.push('disabled');
    }
    
    return classes.join(' ');
  };

  return (
    <div className={`validated-input-container ${className}`}>
      <input
        type={type}
        name={name}
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        className={getInputClasses()}
        style={style}
        {...props}
      />
      
      {showValidation && validationState.isDirty && (
        <div className={`validation-feedback ${validationState.isValid ? 'valid' : 'invalid'}`}>
          <div className="validation-icon">
            {validationState.isValid ? '' : '✗'}
          </div>
          <div className="validation-message">
            {validationState.message}
          </div>
        </div>
      )}
    </div>
  );
};

export default ValidatedInput;
